import { useCallback, useEffect, useRef, useState } from 'react';

type UseMicrophonePreviewOptions = {
	enabled: boolean;
};

type UseMicrophonePreviewResult = {
	stream: MediaStream | null;
	error: string | null;
	release: () => void;
	discard: () => void;
};

export const useMicrophonePreview = ({
	enabled,
}: UseMicrophonePreviewOptions): UseMicrophonePreviewResult => {
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [error, setError] = useState<string | null>(null);
	const streamRef = useRef<MediaStream | null>(null);

	const stopStream = useCallback(() => {
		streamRef.current?.getTracks().forEach((track) => track.stop());
		streamRef.current = null;
		setStream(null);
	}, []);

	const release = useCallback(() => {
		streamRef.current = null;
		setStream(null);
		setError(null);
	}, []);

	useEffect(() => {
		if (!enabled) {
			setError(null);
			return;
		}

		let cancelled = false;

		void (async () => {
			try {
				const nextStream = await navigator.mediaDevices.getUserMedia({
					audio: {
						echoCancellation: true,
						noiseSuppression: true,
					},
				});
				if (cancelled) {
					nextStream.getTracks().forEach((track) => track.stop());
					return;
				}
				stopStream();
				streamRef.current = nextStream;
				setStream(nextStream);
				setError(null);
			} catch {
				if (!cancelled) {
					setStream(null);
					setError('Permita o microfone para visualizar o áudio.');
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [enabled, stopStream]);

	useEffect(() => {
		return () => {
			stopStream();
		};
	}, [stopStream]);

	return { stream, error, release, discard: stopStream };
};
